import { SITEMAP_REPORT } from '@components/Router/constants'
import type { ProjectItem } from '@store/reducers/dashboard/types'
import { managerActions } from '@store/reducers/manager/actions'
import type React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { generatePath } from 'react-router-dom'

import { GtmTriggerName, sendGtmEvent } from '@/constants/gtm'
import { selectIsLoading } from '@/store'
import { isPlainLeftClick } from '@/utils'

// A report has no canvas, so its card opens the report screen instead of the
// editor. Everything else about the card is the same.
const buildTo = ({ id, type }: ProjectItem) =>
  type === 'report' ? generatePath(SITEMAP_REPORT, { uuid: id }) : `/${id}`

// Where a project's <Link> leads and what its click does — shared by the tile
// and the row, which differ only in how they draw the project.
export const useProjectLink = (project: ProjectItem) => {
  const dispatch = useDispatch()

  const loading = useSelector(selectIsLoading(project.id))

  const isReport = project.type === 'report'

  // Обычный клик открывает проект через сагу: сначала загрузка (карточка в
  // loading), потом переход — без глобального лоадера поверх пустого артборда.
  // Cmd-клик, средняя кнопка и клики, которые уже обработало меню «…»,
  // остаются за <Link>.
  const onClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    // Пункты меню «…» рендерятся в портал: по React-дереву они внутри ссылки
    // и всплывают сюда, по DOM — нет. Это не клик по ссылке — гасим его и для
    // <Link>, иначе он сам уведёт в проект.
    if (!event.currentTarget.contains(event.target as Node)) {
      event.preventDefault()

      return
    }

    if (event.defaultPrevented || !isPlainLeftClick(event)) {
      return
    }

    // У отчёта нет холста, который стоило бы загрузить заранее, поэтому его
    // карточка остаётся обычной ссылкой: <Link> уводит сам, сага не при делах.
    if (isReport) {
      sendGtmEvent(GtmTriggerName.REPORT_VIEWED)

      return
    }

    event.preventDefault()

    if (loading) {
      return
    }

    dispatch(
      managerActions.openProjectSaga({ id: project.id, path: buildTo(project) })
    )
  }

  return { loading, onClick, to: buildTo(project) }
}
